/* ============================================================================
 * tool-tutor.js — обучение внутри инструмента: одна дверь, один стиль.
 *
 * ЗАЧЕМ ЭТО ЕСТЬ. У страницы инструмента уже были две поверхности с
 * инструкцией — текстовые шаги над прибором (`tool-howto`) и иллюстрированный
 * проход под ним (`tool-walkthrough`). Обе предназначены ЧИТАТЕЛЮ страницы.
 * Человеку, который уже внутри прибора и не понимает, что нажать, не помогала
 * ни одна: первая прочитана и забыта, вторая — ниже прибора, её не видно.
 *
 * Замер, из которого это выведено (RU-шард, 30 дней, 18.09.2026):
 *   /online-piano    — 3299 сессий, 3491 сыграла ноту, и 2035 из них (58.3%)
 *                      не открыли НИ ОДНОЙ возможности тула (ни разучивания,
 *                      ни записи, ни песен, ни подсказок). Кнопку «❔
 *                      Подсказки» нашли 4.6%. Комментарий #1351 «непонятно что
 *                      делать» — про это, а не про «не нашёл клавиши».
 *   /storyboard-maker — 244 сессии, панель на доску добавили 30 (12.3%),
 *                      экспорт дошёл у 10. Комментарий #1699 «сложно делать
 *                      добавте обучение».
 * Диагнозы разные, поэтому примитив даёт ДВЕ формы помощи, а не одну:
 *
 *   1. ДВЕРЬ (`<x-tool-tutor>`) — одна кнопка в одном и том же месте на всех
 *      тулах: на строке подзаголовка, прямо над прибором. Открывает диалог с
 *      иллюстрированным проходом. Для тула, которому нужно ОБЪЯСНИТЬ работу.
 *   2. ПОДСКАЗКА «ЧТО ДАЛЬШЕ» (`__tutor.nudge`) — одна тихая строка в потоке
 *      самого прибора, в месте, которое называет тул. Один раз, со крестиком,
 *      больше не возвращается. Для тула, где человек уже внутри и работает, но
 *      не знает, что ещё умеет инструмент.
 *
 * ⚠️ ВТОРОЙ КОПИИ КОНТЕНТА НЕ СУЩЕСТВУЕТ. Диалог не рендерится сервером: при
 * первом открытии он КЛОНИРУЕТ блок `tool-walkthrough` со той же страницы.
 * Поэтому шаги, подписи и кадры физически одни и те же, и разъехаться не
 * могут — а страница не тяжелеет ни на байт. По той же причине видео остаётся
 * фасадом: клонируется постер с кнопкой, а обработчик фасада (делегированный,
 * живёт в самом блоке) создаёт проигрыватель по клику и в клоне тоже.
 *
 * ⚠️ БЕЗ JS ДВЕРЬ ВСЁ РАВНО РАБОТАЕТ. Кнопка — это `<a href="#...">` на тот же
 * блок ниже. Не загрузился скрипт или его срезал блокировщик — остаётся
 * обычная ссылка внутри страницы. Апгрейд до диалога делает этот файл.
 *
 * ⛔ Никаких `position:sticky` и автопрокрутки (решение Фарида, повторено
 * трижды). Диалог центрируется, прокручивается его ТЕЛО, подсказка стоит в
 * потоке. Единственное перемещение — возврат фокуса на кнопку при закрытии.
 * ============================================================================ */
(function () {
    'use strict';

    if (window.__tutor) return;

    var SEL_DIALOG = '.tt-dialog';
    var demos = {};            // slug → { label, run }
    var lastFocus = null;

    function slugOf() {
        var m = document.querySelector('meta[name="tool-slug"]');
        return (m && m.content) || '';
    }

    function settings() {
        return window.__toolSettings || { load: function (t, k, f) { return f; }, save: function () {} };
    }

    function track(stage, data) {
        try { if (window.__aeFunnel) window.__aeFunnel(stage, data || {}); } catch (_) {}
    }

    // ── диалог ───────────────────────────────────────────────────────────────

    /** Фокусируемые элементы внутри диалога — для ловушки фокуса. Скрытые не
     *  считаются: клонированный блок может содержать элементы в свёрнутых
     *  панелях, и таб по невидимой кнопке выглядит как потерянный фокус. */
    function focusables(root) {
        var list = root.querySelectorAll(
            'a[href],button:not([disabled]),input:not([disabled]),select,textarea,video,[tabindex]:not([tabindex="-1"])'
        );
        return Array.prototype.filter.call(list, function (el) {
            return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
        });
    }

    /** Наполнение диалога = КЛОН блока прохода. Делается один раз: повторное
     *  открытие показывает то же состояние (раскрытое видео остаётся). */
    function fill(dialog) {
        var body = dialog.querySelector('.tt-dialog-body');
        if (!body || body.getAttribute('data-filled') === '1') return;

        var srcSel = dialog.getAttribute('data-source');
        var src = srcSel ? document.querySelector(srcSel) : null;
        if (src) {
            // Берём содержимое блока, но БЕЗ его заголовка: заголовок диалога
            // уже стоит в шапке, а второй такой же читается как сбой. Вводный
            // абзац переносим — он объясняет, на что человек смотрит («кадры
            // сняты на этой же странице»), и без него диалог начинается сразу
            // с картинки непонятно чего.
            var parts = src.querySelectorAll('.twk-lead, .twk-video, .twk-steps');
            for (var i = 0; i < parts.length; i++) {
                var clone = parts[i].cloneNode(true);
                // Постер в блоке ниже помечен lazy — в диалоге он виден сразу,
                // и ожидание «докрутите до него» здесь работает против нас.
                var img = clone.querySelector && clone.querySelector('img[loading="lazy"]');
                if (img) img.setAttribute('loading', 'eager');
                body.appendChild(clone);
            }
            if (!parts.length) body.appendChild(src.cloneNode(true));
        }
        body.setAttribute('data-filled', '1');
    }

    function open(dialog, via) {
        if (!dialog) return;
        // Повторное открытие уже открытого диалога перезаписало бы lastFocus
        // адресом кнопки ВНУТРИ него, и при закрытии фокус вернулся бы в
        // исчезнувший элемент вместо двери.
        if (!dialog.hidden) return;
        fill(dialog);
        lastFocus = document.activeElement;
        dialog.hidden = false;
        document.documentElement.classList.add('tt-locked');
        var close = dialog.querySelector('[data-tt-close]');
        if (close) close.focus();
        track('tutor_open', { slug: dialog.getAttribute('data-slug') || slugOf(), via: via || 'button' });
        // Открыл — значит дверь нашли; акцент первого визита больше не нужен.
        var slug = dialog.getAttribute('data-slug') || slugOf();
        settings().save(slug, 'tutor_seen', 1);
        document.querySelectorAll('.tt-open[data-slug="' + slug + '"]').forEach(function (b) {
            b.classList.remove('tt-open--fresh');
        });
    }

    function close(dialog) {
        if (!dialog || dialog.hidden) return;
        dialog.hidden = true;
        document.documentElement.classList.remove('tt-locked');
        // Останавливаем всё, что в клоне успело заиграть: закрытый диалог,
        // который продолжает шуметь, — это дефект, а не мелочь.
        dialog.querySelectorAll('video,audio').forEach(function (m) {
            try { m.pause(); } catch (_) {}
        });
        if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (_) {} }
        lastFocus = null;
    }

    // Клик по двери. preventDefault только когда диалог реально есть: если
    // разметки диалога на странице нет, ссылка обязана сработать ссылкой.
    document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('.tt-open');
        if (btn) {
            var dialog = document.querySelector(SEL_DIALOG + '[data-slug="' + btn.getAttribute('data-slug') + '"]');
            if (dialog) { e.preventDefault(); open(dialog, btn.getAttribute('data-via') || 'button'); }
            return;
        }
        var closer = e.target.closest && e.target.closest('[data-tt-close]');
        if (closer) { e.preventDefault(); close(closer.closest(SEL_DIALOG)); return; }
        // Клик по подложке — только если попал именно в неё, а не всплыл изнутри.
        var dlg = e.target.closest && e.target.closest(SEL_DIALOG);
        if (dlg && e.target === dlg) close(dlg);
    }, false);

    document.addEventListener('keydown', function (e) {
        var openDlg = document.querySelector(SEL_DIALOG + ':not([hidden])');
        if (!openDlg) return;
        if (e.key === 'Escape') { e.preventDefault(); close(openDlg); return; }
        if (e.key !== 'Tab') return;
        // Ловушка фокуса: без неё таб уходит в страницу за диалогом, и человек
        // на клавиатуре теряет и диалог, и место, откуда его открыл.
        var f = focusables(openDlg);
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }, false);

    // ── акцент первого визита ────────────────────────────────────────────────
    // Дверь у вернувшегося и у нового посетителя — ОДИН элемент одного размера:
    // меняется только цвет. Подменять текст нельзя — страница отдаётся из
    // PageCache всем одинаковой, надпись правил бы JS, и строка на телефоне
    // переносилась бы на вторую, сдвигая прибор вниз уже после первой отрисовки.
    function markFresh() {
        document.querySelectorAll('.tt-open[data-slug]').forEach(function (b) {
            var slug = b.getAttribute('data-slug');
            if (!settings().load(slug, 'tutor_seen', 0)) b.classList.add('tt-open--fresh');
        });
    }

    // ── подсказка «что дальше» ───────────────────────────────────────────────
    /**
     * __tutor.nudge({ slug, key, text, actions:[{label, run}], anchor })
     *
     * Одна строка в потоке прибора. `key` — своя память на каждый повод, чтобы
     * закрытый совет не возвращался, а другой поводе мог прийти. `anchor` —
     * элемент или селектор, ВНУТРЬ которого встать; по умолчанию элемент с
     * `[data-tutor-nudge]`. Показывается один раз за всё время (не за сессию):
     * совет, который человек уже отклонил, на второй день не становится умнее.
     */
    function nudge(opts) {
        opts = opts || {};
        var slug = opts.slug || slugOf();
        var key = 'tutor_nudge_' + (opts.key || 'next');
        if (!opts.text) return null;
        if (settings().load(slug, key, 0)) return null;

        var host = typeof opts.anchor === 'string' ? document.querySelector(opts.anchor)
            : (opts.anchor || document.querySelector('[data-tutor-nudge]'));
        if (!host) return null;

        var box = document.createElement('div');
        box.className = 'tt-nudge';
        box.setAttribute('role', 'status');

        var txt = document.createElement('span');
        txt.className = 'tt-nudge-txt';
        txt.textContent = opts.text;
        box.appendChild(txt);

        (opts.actions || []).forEach(function (a) {
            if (!a || !a.label) return;
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'tt-nudge-btn';
            b.textContent = a.label;
            b.addEventListener('click', function () {
                settings().save(slug, key, 1);
                track('tutor_nudge_taken', { slug: slug, key: opts.key || 'next', act: a.id || a.label });
                remove();
                try { if (typeof a.run === 'function') a.run(); } catch (_) {}
            });
            box.appendChild(b);
        });

        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'tt-nudge-x';
        x.setAttribute('aria-label', opts.closeLabel || 'Close');
        x.textContent = '×';
        x.addEventListener('click', function () {
            settings().save(slug, key, 1);
            track('tutor_nudge_dismissed', { slug: slug, key: opts.key || 'next' });
            remove();
        });
        box.appendChild(x);

        function remove() {
            if (box.parentNode) box.parentNode.removeChild(box);
        }

        // ⚠️ Встаём в поток, а не поверх: всплывающая над прибором подсказка
        // закрыла бы то, о чём говорит. Контейнер-хозяин объявляет место сам.
        host.appendChild(box);
        track('tutor_nudge_shown', { slug: slug, key: opts.key || 'next' });
        return box;
    }

    // ── внешний интерфейс ────────────────────────────────────────────────────
    window.__tutor = {
        open: function (slug, via) {
            return open(document.querySelector(SEL_DIALOG + '[data-slug="' + (slug || slugOf()) + '"]'), via || 'api');
        },
        close: function (slug) {
            return close(document.querySelector(SEL_DIALOG + '[data-slug="' + (slug || slugOf()) + '"]'));
        },
        /** Есть ли на этой странице дверь — тулу это нужно, чтобы не обещать
         *  в своей подсказке «посмотрите, как это работает», когда смотреть
         *  нечего (проход ещё не снят в этой локали). */
        available: function (slug) {
            return !!document.querySelector(SEL_DIALOG + '[data-slug="' + (slug || slugOf()) + '"]');
        },
        /** Тул объявляет «попробовать на примере». Диалог покажет кнопку, если
         *  демо зарегистрировано ДО первого открытия или после — неважно. */
        demo: function (slug, def) {
            if (!def || typeof def.run !== 'function') return;
            demos[slug] = def;
            var dlg = document.querySelector(SEL_DIALOG + '[data-slug="' + slug + '"]');
            if (!dlg) return;
            var slot = dlg.querySelector('[data-tt-demo]');
            if (!slot) return;
            slot.hidden = false;
            slot.textContent = def.label || slot.getAttribute('data-label') || '';
            slot.onclick = function () {
                track('tutor_demo_run', { slug: slug });
                close(dlg);
                try { def.run(); } catch (_) {}
            };
        },
        nudge: nudge,
        /** Забыть отметки — для ручной проверки и для стендов. */
        reset: function (slug) {
            var s = slug || slugOf();
            settings().save(s, 'tutor_seen', 0);
            try {
                Object.keys(localStorage).forEach(function (k) {
                    if (k.indexOf('ts_' + s + '_tutor_nudge_') === 0) localStorage.removeItem(k);
                });
            } catch (_) {}
        },
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', markFresh);
    } else {
        markFresh();
    }
})();
